import { memo, useEffect, useRef } from 'react';

import { getLocalStorageItem, removeLocalStorageItem } from '@revenuemarket/browser';
import { GrayColors, typographyStyle, Input, Button, CheckboxV2, Flex } from '@revenuemarket/cds';
import { useLoading } from '@revenuemarket/react';
import { Typography } from '@revenuemarket/ui';
import { formatPhoneNumberWithHyphen } from '@revenuemarket/utils';
import { Form, InputRef } from 'antd';
import { Rule } from 'antd/es/form';
import * as yup from 'yup';

import { SignupPasswordStepModel } from '~components/Auth/signup/steps/SignupPasswordStep';
import AgreementsCheckboxGroup from '~components/ui/checkbox/AgreementsCheckboxGroup';
import { COMMON_YUP } from '~constants/yup-scheme';
import { useAgreements } from '~hooks/queries/auth/query';
import { css } from '~libs/emotion';
import { AuthModel } from '~models/auth';

export interface SignupInfoStepForm extends AuthModel.SignUpRequest {
  passwordConfirm: string;
}

const SignupInfoStepScheme = yup.object({
  email: COMMON_YUP.email,
  password: COMMON_YUP.password,
  phoneNumber: COMMON_YUP.phoneNumber,
  name: yup
    .string()
    .required('이름을 2자 이상 입력해주세요.')
    .min(2, '이름을 2자 이상 입력해주세요.'),
});

const getSchemeRule = (field: string): Rule => ({
  validator: async (_, value) => {
    await SignupInfoStepScheme.validateAt(field, { [field]: value });
  },
});

export interface SignupInfoStepModel extends SignupPasswordStepModel {
  password?: string;
  name?: string | null;
  phoneNumber?: string | null;
  fromInvitation?: boolean;
}

interface Props {
  model: SignupInfoStepModel;
  onFinish: (values: SignupInfoStepForm) => void | Promise<void>;
  fromInvitation?: boolean;
}

const SignupInfoStep = ({ model, onFinish: onFinishProp, fromInvitation }: Props) => {
  const [form] = Form.useForm<SignupInfoStepForm>();
  const inputRef = useRef<InputRef | null>(null);
  const phoneInputRef = useRef<InputRef | null>(null);

  const { data: agreementsResponse } = useAgreements();
  const agreements = agreementsResponse?.Data?.data;

  const [isLoading, startTransaction] = useLoading();

  const onFinish = (values: SignupInfoStepForm) => {
    const mobileInvitationToken = getLocalStorageItem<string>('mobileInvitationToken');
    if (mobileInvitationToken) {
      removeLocalStorageItem('mobileInvitationToken');
    }
    const payload = {
      ...values,
      mobileInvitationToken: mobileInvitationToken ?? undefined,
    };

    startTransaction(() => Promise.resolve(onFinishProp(payload)));
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (agreements) {
      form.setFieldsValue({
        agreements: agreements
          .filter((agreement) => agreement.required)
          .map((agreement) => agreement.id),
      });
    }
  }, [agreements]);

  return (
    <Flex
      direction="column"
      gap={12}
      className={css`
        width: 440px;
        @media (max-width: 768px) {
          max-width: 440px;
          width: 100%;
          align-self: center;
        }
      `}
    >
      <Typography
        variant="body1"
        color={GrayColors.gray900}
        style={{
          ...typographyStyle['24_600'],
          paddingBottom: 12,
        }}
      >
        가입을 완료해 주세요
      </Typography>

      <Form
        form={form}
        onFinish={onFinish}
        initialValues={{
          ...model,
          agreements: [],
          phoneNumber: model?.phoneNumber ?? '010',
        }}
        className={css`
          &&& .ant-form-item-explain-error {
            font-size: 14px;
          }
        `}
      >
        <Flex direction="column" gap={12}>
          <Form.Item name="email" rules={[getSchemeRule('email')]} hidden={true}>
            <Input disabled size="large" type="email" placeholder="회사 이메일" />
          </Form.Item>

          <Form.Item name="password" rules={[getSchemeRule('password')]} hidden={true}>
            <Input size="large" type="password" placeholder="비밀번호" />
          </Form.Item>

          <Flex direction="column" gap={8}>
            <Typography
              variant="body1"
              color={GrayColors.gray700}
              style={{
                ...typographyStyle['16_400'],
              }}
            >
              이름
            </Typography>

            <Form.Item name="name" rules={[getSchemeRule('name')]} style={{ margin: 0 }}>
              <Input
                inputSize="xlarge"
                type="name"
                placeholder="이름을 입력해주세요"
                deformatter={(value) => {
                  const hasKorean = /[ㄱ-ㅎ가-힣]/.test(value);
                  if (hasKorean) {
                    // 한글이 있으면 스페이스 제외
                    return value.replace(/[^ㄱ-ㅎ가-힣]/g, '');
                  } else {
                    // 영어만 있으면 스페이스 허용
                    return value.replace(/[^a-zA-Z\s]/g, '');
                  }
                }}
                ref={inputRef}
                onKeyDown={(e) => {
                  if (e.key === 'Tab' && !e.shiftKey) {
                    setTimeout(() => {
                      if (phoneInputRef.current && phoneInputRef.current.input) {
                        const inputElem = phoneInputRef.current.input;
                        const len = inputElem.value.length;
                        inputElem.setSelectionRange(len, len);
                      }
                    }, 0);
                  }
                }}
              />
            </Form.Item>
          </Flex>

          <Flex direction="column" gap={8}>
            <Typography
              variant="body1"
              color={GrayColors.gray700}
              style={{
                ...typographyStyle['16_400'],
              }}
            >
              휴대폰 번호
            </Typography>

            <Form.Item
              name="phoneNumber"
              rules={[getSchemeRule('phoneNumber')]}
              style={{ margin: 0 }}
            >
              <Input
                inputSize="xlarge"
                type="phoneNumber"
                placeholder="휴대폰번호"
                ref={phoneInputRef}
                onKeyDown={(e) => {
                  const input = e.target as HTMLInputElement;
                  const value = input.value;

                  // 백스페이스 키이고 하이픈으로 끝나는 경우
                  if (e.key === 'Backspace' && value.endsWith('-')) {
                    e.preventDefault(); // 기본 동작 방지
                    // 하이픈과 그 앞의 숫자 하나를 함께 지움
                    input.value = value.slice(0, -2);
                  }
                }}
                formatter={formatPhoneNumberWithHyphen}
                deformatter={(value) => value.replace(/[^\d]/g, '')}
              />
            </Form.Item>
          </Flex>

          <Flex direction="column" gap={8} style={{ padding: '8px 0' }}>
            <Form.Item<SignupInfoStepForm> noStyle dependencies={['agreements']}>
              {({ getFieldValue, setFieldsValue }) => {
                const agreementValues = getFieldValue('agreements');
                const checked = agreementValues != null && agreementValues.length > 0;
                const intermediate =
                  agreements != null &&
                  agreementValues.length > 0 &&
                  agreements.length !== agreementValues.length;

                return (
                  <CheckboxV2
                    indeterminate={intermediate}
                    checked={checked}
                    className={css`
                      .ant-checkbox ~ span {
                        width: 100%;
                        padding-left: 8px;
                      }
                    `}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFieldsValue({
                          agreements: agreements?.map((agreement) => agreement.id),
                        });
                      } else {
                        setFieldsValue({ agreements: [] });
                      }
                    }}
                    label="전체 동의하기"
                  ></CheckboxV2>
                );
              }}
            </Form.Item>
            <Form.Item name="agreements" style={{ margin: 0 }}>
              <AgreementsCheckboxGroup
                size="large"
                agreements={agreements}
                className={css`
                  padding-left: 24px;
                `}
                onChange={(value) => {
                  form.setFieldsValue({
                    agreements: value,
                  });
                }}
              />
            </Form.Item>
          </Flex>

          <Form.Item<SignupInfoStepForm> noStyle shouldUpdate>
            {({ getFieldsValue }) => {
              const value = getFieldsValue();
              const valid = (() => {
                try {
                  if (fromInvitation) {
                    SignupInfoStepScheme.validateSync({
                      ...model,
                      ...value,
                    });
                  } else {
                    SignupInfoStepScheme.validateSync(value);
                  }

                  return agreements?.every(
                    (agreement) => !agreement.required || value.agreements.includes(agreement.id),
                  );
                } catch {
                  return false;
                }
              })();

              return (
                <Button
                  buttonType="primary"
                  buttonSize="xlarge"
                  onClick={form.submit}
                  disabled={!valid}
                  loading={isLoading}
                  className={css`
                    width: 100%;
                  `}
                >
                  가입하기
                </Button>
              );
            }}
          </Form.Item>
        </Flex>
      </Form>
    </Flex>
  );
};

export default memo(SignupInfoStep);
